import { ExternalLink, FileText, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    deleteDispatch,
    deleteDocument,
    documentUrl,
    fetchChallanRefs,
    fetchCompanies,
    fetchDocuments,
    fetchTransporters,
    saveDispatch,
    uploadDocument,
    type Dispatch,
    type DispatchDocument,
    type Transporter,
    STATUS_LABEL,
} from './dispatchApi';

const selectCls =
    'h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring';

type FormState = {
    dispatch_date: string;
    company_id: string;
    source_location: string;
    destination: string;
    transporter_id: string;
    vehicle_no: string;
    vehicle_length_ft: string;
    driver_name: string;
    driver_mobile: string;
    tonnage: string;
    freight_cost: string;
    status: string;
    payment_status: string;
    paid_amount: string;
    delivery_challan_id: string;
    notes: string;
};

const toForm = (d?: Partial<Dispatch> | null): FormState => ({
    dispatch_date: d?.dispatch_date ?? new Date().toISOString().slice(0, 10),
    company_id: d?.company_id?.toString() ?? '',
    source_location: d?.source_location ?? '',
    destination: d?.destination ?? '',
    transporter_id: d?.transporter_id?.toString() ?? '',
    vehicle_no: d?.vehicle_no ?? '',
    vehicle_length_ft: d?.vehicle_length_ft?.toString() ?? '',
    driver_name: d?.driver_name ?? '',
    driver_mobile: d?.driver_mobile ?? '',
    tonnage: d?.tonnage?.toString() ?? '',
    freight_cost: d?.freight_cost?.toString() ?? '',
    status: d?.status ?? 'planned',
    payment_status: d?.payment_status ?? 'due',
    paid_amount: d?.paid_amount?.toString() ?? '',
    delivery_challan_id: d?.delivery_challan_id?.toString() ?? '',
    notes: d?.notes ?? '',
});

const num = (s: string) => (s === '' ? null : Number(s));

const Field = ({
    label,
    children,
    span = 1,
}: {
    label: string;
    children: React.ReactNode;
    span?: 1 | 2;
}) => (
    <div className={`space-y-1 ${span === 2 ? 'col-span-2' : ''}`}>
        <Label className="text-xs text-muted-foreground">{label}</Label>
        {children}
    </div>
);

export const DispatchFormDialog = ({
    open,
    onOpenChange,
    record,
    initial,
    onSaved,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    /** Existing dispatch to edit. */
    record?: Dispatch | null;
    /** Prefill values for a new entry (e.g. from a booked enquiry). */
    initial?: Partial<Dispatch> | null;
    onSaved: () => void;
}) => {
    const navigate = useNavigate();
    const [f, setF] = useState<FormState>(toForm(record ?? initial));
    const [transporters, setTransporters] = useState<Transporter[]>([]);
    const [companies, setCompanies] = useState<{ id: number; name: string }[]>([]);
    const [challans, setChallans] = useState<{ id: number; reference: string }[]>([]);
    const [docs, setDocs] = useState<DispatchDocument[]>([]);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        setF(toForm(record ?? initial));
        Promise.all([
            fetchTransporters(),
            fetchCompanies(),
            fetchChallanRefs(),
        ])
            .then(([t, c, d]) => {
                setTransporters(t);
                setCompanies(c);
                setChallans(d);
            })
            .catch(e => toast.error(e.message));
        if (record?.id) fetchDocuments(record.id).then(setDocs).catch(() => {});
        else setDocs([]);
    }, [open, record, initial]);

    const set = (k: keyof FormState, v: string) => setF(p => ({ ...p, [k]: v }));

    const submit = async () => {
        setBusy(true);
        try {
            const transporter = transporters.find(
                t => t.id === Number(f.transporter_id),
            );
            let payment = f.payment_status;
            const freight = num(f.freight_cost) ?? 0;
            const paid = num(f.paid_amount) ?? 0;
            if (payment !== 'paid' && freight > 0 && paid >= freight) payment = 'paid';
            else if (payment === 'due' && paid > 0) payment = 'partial';
            await saveDispatch({
                id: record?.id,
                dispatch_no: record?.dispatch_no ?? initial?.dispatch_no ?? undefined,
                enquiry_id: record?.enquiry_id ?? initial?.enquiry_id ?? null,
                dispatch_date: f.dispatch_date,
                company_id: num(f.company_id),
                source_location: f.source_location.trim() || null,
                destination: f.destination.trim() || null,
                transporter_id: transporter?.id ?? null,
                transporter_name: transporter?.name ?? record?.transporter_name ?? null,
                vehicle_no: f.vehicle_no.trim() || null,
                vehicle_length_ft: num(f.vehicle_length_ft),
                driver_name: f.driver_name.trim() || null,
                driver_mobile: f.driver_mobile.trim() || null,
                tonnage: num(f.tonnage),
                freight_cost: num(f.freight_cost),
                status: f.status as Dispatch['status'],
                payment_status: payment as Dispatch['payment_status'],
                paid_amount: paid,
                delivery_challan_id: num(f.delivery_challan_id),
                notes: f.notes.trim() || null,
            });
            toast.success(record ? 'Entry updated' : 'Entry created');
            onOpenChange(false);
            onSaved();
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!record || !window.confirm('Delete this consignment entry?')) return;
        try {
            await deleteDispatch(record.id);
            onOpenChange(false);
            onSaved();
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    const onFile = async (file?: File) => {
        if (!file || !record) return;
        try {
            await uploadDocument(record.id, file, 'general');
            setDocs(await fetchDocuments(record.id));
            onSaved();
            toast.success('Document uploaded');
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    const openDoc = async (d: DispatchDocument) => {
        if (!d.file_path) return;
        try {
            window.open(await documentUrl(d.file_path), '_blank');
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-primary">
                        {record ? `Edit Entry ${record.dispatch_no ?? ''}` : 'New Entry'}
                    </DialogTitle>
                    <DialogDescription>
                        Consignment details, freight and payment.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Date">
                        <Input type="date" id="dsp-date" value={f.dispatch_date}
                            onChange={e => set('dispatch_date', e.target.value)} />
                    </Field>
                    <Field label="Customer / Company">
                        <select className={selectCls} id="dsp-company" value={f.company_id}
                            onChange={e => set('company_id', e.target.value)}>
                            <option value="">— none —</option>
                            {companies.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="From">
                        <Input id="dsp-from" value={f.source_location}
                            onChange={e => set('source_location', e.target.value)} />
                    </Field>
                    <Field label="To">
                        <Input id="dsp-to" value={f.destination}
                            onChange={e => set('destination', e.target.value)} />
                    </Field>
                    <Field label="Transporter">
                        <select className={selectCls} id="dsp-transporter" value={f.transporter_id}
                            onChange={e => set('transporter_id', e.target.value)}>
                            <option value="">— none —</option>
                            {transporters.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Vehicle no.">
                        <Input id="dsp-vehicle" value={f.vehicle_no}
                            onChange={e => set('vehicle_no', e.target.value)} />
                    </Field>
                    <Field label="Vehicle length (ft)">
                        <Input type="number" value={f.vehicle_length_ft}
                            onChange={e => set('vehicle_length_ft', e.target.value)} />
                    </Field>
                    <Field label="Tonnage (MT)">
                        <Input type="number" step="0.001" value={f.tonnage}
                            onChange={e => set('tonnage', e.target.value)} />
                    </Field>
                    <Field label="Driver name">
                        <Input value={f.driver_name}
                            onChange={e => set('driver_name', e.target.value)} />
                    </Field>
                    <Field label="Driver mobile">
                        <Input value={f.driver_mobile}
                            onChange={e => set('driver_mobile', e.target.value)} />
                    </Field>
                    <Field label="Freight cost (₹)">
                        <Input type="number" id="dsp-freight" value={f.freight_cost}
                            onChange={e => set('freight_cost', e.target.value)} />
                    </Field>
                    <Field label="Paid amount (₹)">
                        <Input type="number" value={f.paid_amount}
                            onChange={e => set('paid_amount', e.target.value)} />
                    </Field>
                    <Field label="Shipment status">
                        <select className={selectCls} value={f.status}
                            onChange={e => set('status', e.target.value)}>
                            {Object.entries(STATUS_LABEL).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Payment status">
                        <select className={selectCls} value={f.payment_status}
                            onChange={e => set('payment_status', e.target.value)}>
                            <option value="due">Payment due</option>
                            <option value="partial">Partially paid</option>
                            <option value="paid">Paid</option>
                        </select>
                    </Field>
                    <Field label="Linked Delivery Challan" span={2}>
                        <div className="flex gap-2">
                            <select className={selectCls} value={f.delivery_challan_id}
                                onChange={e => set('delivery_challan_id', e.target.value)}>
                                <option value="">— not linked —</option>
                                {challans.map(c => (
                                    <option key={c.id} value={c.id}>{c.reference}</option>
                                ))}
                            </select>
                            {f.delivery_challan_id && (
                                <Button type="button" variant="outline" size="icon"
                                    title="Open delivery challan"
                                    onClick={() => navigate(`/delivery_challans/${f.delivery_challan_id}/show`)}>
                                    <ExternalLink />
                                </Button>
                            )}
                        </div>
                    </Field>
                    <Field label="Notes" span={2}>
                        <textarea
                            className="min-h-16 w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                            value={f.notes}
                            onChange={e => set('notes', e.target.value)}
                        />
                    </Field>
                </div>

                {record && (
                    <div className="rounded-xl border p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold">Documents</Label>
                            <input ref={fileRef} type="file" className="hidden"
                                onChange={e => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
                            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                                <Upload /> Upload
                            </Button>
                        </div>
                        {docs.length === 0 && (
                            <p className="text-xs text-muted-foreground">No documents uploaded.</p>
                        )}
                        {docs.map(d => (
                            <div key={d.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-1.5">
                                <button className="flex items-center gap-2 text-sm hover:text-primary truncate"
                                    onClick={() => openDoc(d)}>
                                    <FileText className="size-4" /> {d.file_name}
                                </button>
                                <Button variant="ghost" size="icon-sm" className="text-destructive"
                                    onClick={async () => {
                                        await deleteDocument(d);
                                        setDocs(await fetchDocuments(record.id));
                                        onSaved();
                                    }}>
                                    <Trash2 />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                <DialogFooter className="sm:justify-between">
                    {record ? (
                        <Button variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={remove}>
                            <Trash2 /> Delete
                        </Button>
                    ) : <span />}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button id="save-dispatch-btn" onClick={submit} disabled={busy}>
                            {record ? 'Save Changes' : 'Create Entry'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
